import { useParams } from 'react-router-dom';
import { Bot, Calculator, ExternalLink, Info, Layers, Plug, Receipt, Scale, Trophy, Video, Wallet } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CheckList, CtaPanel, DataTable, FaqSection, HeroActions, IconCardGrid, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink } from '@/components/page/PageKit';
import { BEATVIEW_PRICING, PRICE_INDEX, annualTeamCost, formatMoney, getPricingGuide, pricingGuideUrl, type PricingGuide } from '@/data/pricingGuides';
import { articleSchema, faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';
import { cn } from '@/lib/utils';

const TEAM_SIZES = [1, 5, 15];
const EXTRA_ICONS = [Receipt, Wallet, Plug, Bot, Calculator];

function GuidePage({ guide }: { guide: PricingGuide }) {
  const url = pricingGuideUrl(guide.slug);
  const headline = `${guide.vendor} pricing in 2026: plans, costs and what’s included`;
  useSEO({ title: guide.seoTitle, description: guide.description, canonicalUrl: url, ogType: 'article' });

  const seatPlans = guide.plans.filter(p => p.seatAnnual);
  // Vendors that do not split monthly and annual billing get one price column.
  const singlePrice = guide.plans.every(p => p.annual === null);
  const peers = PRICE_INDEX.filter(row => row.category === guide.category && row.name !== guide.vendor);
  const self = PRICE_INDEX.find(row => row.name === guide.vendor);
  const beatview = PRICE_INDEX.find(row => row.isBeatview);
  const compareRows = [self, ...peers, ...(guide.category === 'AI interviews and screening' && beatview ? [beatview] : [])].filter(Boolean) as typeof PRICE_INDEX;
  const angle = beatviewAngle(guide);

  return (
    <PageShell>
      <PageStructuredData name={headline} description={guide.description} url={url} dateModified={guide.reviewed} breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: 'Recruitment software pricing', url: 'https://beatview.ai/guides/recruitment-software-pricing' }, { name: `${guide.vendor} pricing` }]} />
      <StructuredData data={[articleSchema({ headline, description: guide.description, url, datePublished: guide.reviewed, about: { '@type': 'SoftwareApplication', name: guide.vendor, url: guide.website, applicationCategory: 'BusinessApplication' } }), faqSchema(guide.faqs)]} />
      <article>
        <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: 'Software pricing', href: '/guides/recruitment-software-pricing' }, { name: `${guide.vendor} pricing` }]} eyebrow={`Pricing guide · Checked ${formatDate(guide.reviewed)}`} title={<>{guide.vendor} pricing in 2026: <span className="bv-hero-highlight">plans, costs and what’s included</span></>}>
          <InShort label="The short answer">{guide.shortAnswer}</InShort>
          <div className="mt-7"><HeroActions primaryLabel="Try Beatview free" secondary={{ label: 'See every plan', href: '#plans' }} note={`Prices read on ${guide.vendor}’s own pricing page on ${formatDate(guide.reviewed)}. Beatview is not affiliated with ${guide.vendor}; confirm current terms with them before you buy.`} /></div>
        </PageHero>

        <Section tone="wash" id="plans" labelledBy="plans-title">
          <SectionHeading id="plans-title" eyebrow="Plans and prices" title={`${guide.vendor} plans at a glance`} description={guide.model} />
          <Reveal delay={0.1} className="mt-10">
            <DataTable
              caption={`${guide.vendor} plans and list prices, checked ${formatDate(guide.reviewed)}`}
              columns={singlePrice ? ['Plan', 'Price', 'Who it’s for'] : ['Plan', 'Monthly billing', 'Annual billing', 'Who it’s for']}
              rows={guide.plans.map(p => singlePrice ? [p.name, p.monthly ?? 'Not published', p.audience] : [p.name, p.monthly ?? 'Not offered', p.annual ?? 'Not offered', p.audience])}
              minWidth={760}
            />
          </Reveal>
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {guide.plans.map((plan, i) => (
              <Reveal key={plan.name} delay={(i % 3) * 0.05}>
                <article className="flex h-full flex-col rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                  <h3 className="font-display text-xl font-semibold text-bv-ink">{plan.name}</h3>
                  <p className="mt-1 text-[15px] font-semibold text-bv-blue">{plan.annual ?? plan.monthly ?? 'Price on request'}</p>
                  {plan.annual && plan.monthly && plan.annual !== plan.monthly && <p className="mt-0.5 text-[13px] text-bv-mute">Billed annually, or {plan.monthly} billed monthly</p>}
                  <h4 className="mt-5 text-[13px] font-semibold uppercase tracking-wider text-bv-mute">Includes</h4>
                  <CheckList className="mt-3" items={plan.included} />
                  {plan.limits && plan.limits.length > 0 && (
                    <>
                      <h4 className="mt-5 text-[13px] font-semibold uppercase tracking-wider text-bv-mute">Limits</h4>
                      <CheckList className="mt-3" icon="minus" items={plan.limits} />
                    </>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </Section>

        {seatPlans.length > 0 && (
          <Section tone="white" labelledBy="team-title">
            <SectionHeading id="team-title" eyebrow="What a team pays" title={`What ${guide.vendor} costs a year for your team`} description={`List price: ${singlePrice ? 'the published price per user a month' : 'the annual-billing price per user a month'} × users × 12. Before tax, discounts, add-ons and any onboarding fee.`} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable
                caption={`${guide.vendor} annual list price by team size`}
                columns={['Plan', ...TEAM_SIZES.map(n => `${n} ${n === 1 ? 'recruiter' : 'recruiters'}`)]}
                rows={seatPlans.map(p => [p.name, ...TEAM_SIZES.map(n => p.maxSeats && n > p.maxSeats ? `Not available (max ${p.maxSeats})` : formatMoney(annualTeamCost(p.seatAnnual as number, n), guide.currency))])}
                minWidth={640}
              />
            </Reveal>
          </Section>
        )}

        <Section tone="base" labelledBy="extras-title">
          <SectionHeading id="extras-title" eyebrow="Beyond the plan price" title="Other costs to budget for" description={`What ${guide.vendor}’s own pages say about add-ons, fees, trials and contract terms.`} />
          <IconCardGrid className="mt-12" columns={guide.extras.length === 4 ? 2 : 3} items={guide.extras.map((e, i) => ({ icon: EXTRA_ICONS[i % EXTRA_ICONS.length], title: e.title, body: e.body }))} />
        </Section>

        {compareRows.length > 1 && (
          <Section tone="white" labelledBy="compare-title">
            <SectionHeading id="compare-title" eyebrow="Compared" title={`How ${guide.vendor}’s price compares`} description="Entry prices as each vendor publishes them. Features differ, so compare what you actually need." />
            <Reveal delay={0.1} className="mt-10">
              <DataTable
                caption={`${guide.vendor} entry price compared with similar tools`}
                columns={['Tool', 'Entry price', 'Free plan or trial', 'Pricing guide']}
                rows={compareRows.map(row => [
                  <span key="n" className={cn(row.name === guide.vendor && 'text-bv-blue')}>{row.name}{row.isBeatview && <span className="ml-2 rounded-full bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Our product</span>}</span>,
                  row.entry,
                  row.free,
                  row.href ? <SmartLink key="l" href={row.href} className="font-semibold text-bv-blue hover:underline">{row.name === guide.vendor ? 'This page' : 'Read'}</SmartLink> : '—',
                ])}
                minWidth={720}
              />
            </Reveal>
          </Section>
        )}

        <Section tone="dark" labelledBy="beatview-title">
          <SectionHeading tone="dark" id="beatview-title" eyebrow={angle.eyebrow} title={angle.title} description={angle.description} />
          <IconCardGrid tone="dark" className="mt-12" items={[
            { icon: Wallet, title: 'What it costs', body: <>{BEATVIEW_PRICING.free}. {BEATVIEW_PRICING.pro}. {BEATVIEW_PRICING.note}</> , href: '/pricing', link: 'See Beatview pricing' },
            { icon: Plug, title: 'How it connects', body: `Connect Beatview through Zapier on Enterprise, or ask us to enable a native integration. The MCP server for Claude, ChatGPT and Gemini is on every plan.`, href: '/integrations', link: 'See integrations' },
            { icon: Video, title: 'What it adds', body: 'A job-match score with reasons for every resume, timed one-way video interviews scored answer by answer, and password-protected client sharing. Recruiters make every decision.', href: '/for-recruiting-agencies', link: 'How agencies use it' },
          ]} />
        </Section>

        <FaqSection title={`${guide.vendor} pricing: frequently asked questions`} items={guide.faqs} idPrefix={`${guide.slug}-faq`} tone="base" />

        <Section tone="white" narrow id="sources" labelledBy="sources-title">
          <SectionHeading id="sources-title" eyebrow="Sources" title="Where these prices come from" description={`Only ${guide.vendor}’s own pages, read on ${formatDate(guide.reviewed)}. Prices change; check them before you buy.`} />
          <Reveal delay={0.1}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {guide.sources.map(source => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="group flex items-center justify-between gap-3 rounded-2xl border border-bv-blue-line bg-white px-5 py-4 font-semibold text-bv-ink transition-colors hover:border-bv-blue/40 hover:text-bv-blue">{source.label}<ExternalLink className="h-4 w-4 shrink-0" /></a></li>
              ))}
            </ul>
            <div className="mt-5 flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-base p-4 text-[13.5px] leading-6 text-bv-ink2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" />
              <p>Beatview publishes this guide and sells a screening tool that {angle.relation}. We list only published facts; where {guide.vendor} does not say, we say so. Spotted a change? <SmartLink href="/contact" className="font-semibold text-bv-blue hover:underline">Tell us</SmartLink>.</p>
            </div>
          </Reveal>
        </Section>
      </article>

      <RelatedLinks title="Keep comparing" tone="base" items={[
        { title: 'Recruitment software pricing', body: 'What every agency ATS and AI tool costs, side by side.', href: '/guides/recruitment-software-pricing', icon: Scale },
        ...guide.links.slice(0, 2).map((link, i) => ({ title: link.label, body: link.body, href: link.href, icon: i === 0 ? Layers : Trophy })),
        { title: 'Screening cost calculator', body: 'Estimate the hours your desk spends on first-pass screening.', href: '/screening-cost-calculator', icon: Calculator },
      ]} />
      <CtaPanel eyebrow="Start free" title={<>Screen your next role <span className="bv-hero-highlight">before you change any software.</span></>} description="Beatview’s Free plan covers one active job with unlimited candidates. Pro adds AI video interviews." secondary={{ label: 'Compare all pricing', href: '/guides/recruitment-software-pricing' }} />
    </PageShell>
  );
}

function beatviewAngle(guide: PricingGuide) {
  if (guide.category === 'Agency ATS and CRM') return {
    eyebrow: `Beside ${guide.vendor}`,
    title: <>Add AI screening and interviews <span className="bv-hero-highlight">without changing your ATS</span></>,
    description: `Beatview scores every application against your criteria and interviews the shortlist by structured video, then hands the evidence to your recruiters. ${guide.vendor} stays your system of record.`,
    relation: 'works beside ATS platforms like this one',
  };
  if (guide.category === 'AI interviews and screening') return {
    eyebrow: 'An alternative',
    title: <>Screening and AI interviews <span className="bv-hero-highlight">at a published price</span></>,
    description: 'Beatview screens every application, runs structured one-way AI video interviews and shares candidates with clients, with its prices on its website.',
    relation: 'competes with tools like this one',
  };
  return {
    eyebrow: `After ${guide.vendor}`,
    title: <>Once candidates apply, <span className="bv-hero-highlight">screen and interview them automatically</span></>,
    description: `${guide.vendor} helps you find people. Beatview takes over when they apply: it scores every application against your criteria, runs structured video interviews and shows the evidence behind each score.`,
    relation: 'works after sourcing tools like this one',
  };
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function formatDate(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

export function PricingGuidePage({ slug }: { slug: string }) {
  const guide = getPricingGuide(slug);
  return guide ? <GuidePage guide={guide} /> : <NotFound />;
}

export default function PricingGuideRoute() {
  const { guide } = useParams<{ guide: string }>();
  return <PricingGuidePage slug={guide ?? ''} />;
}
